import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Table,
  Tag,
  Space,
  Spin,
  Image,
  message,
  Modal,
  Checkbox,
  Statistic,
  Result
} from 'antd';
import {
  ArrowLeftOutlined,
  GiftOutlined,
  CalendarOutlined,
  DownloadOutlined,
  CopyOutlined,
  EyeOutlined
} from '@ant-design/icons';
import axios from '../../utils/axios';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';

const { Title, Text } = Typography;

const VoucherDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [voucherData, setVoucherData] = useState(null);
  const [myVouchers, setMyVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [exportSelectedFields, setExportSelectedFields] = useState([
    'Code', 'Serial Number', 'PIN', 'Issued At', 'Order Number'
  ]);

  // Check if navigated from My Vouchers page
  const fromMyVouchers = location.state?.fromMyVouchers;
  const passedVouchers = location.state?.myVouchers;

  // Field definitions
  const exportFields = [
    { label: 'Code', value: 'Code' },
    { label: 'Serial Number', value: 'Serial Number' },
    { label: 'PIN', value: 'PIN' },
    { label: 'Issued At', value: 'Issued At' },
    { label: 'Order Number', value: 'Order Number' }
  ];

  // Get voucher code details
  const fetchVoucherDetails = async () => {
    try {
      setLoading(true);
      
      if (fromMyVouchers && passedVouchers) {
        // Data from My Vouchers page
        setMyVouchers(passedVouchers);
        setVoucherData({
          voucherName: passedVouchers[0]?.voucherName,
          brand: passedVouchers[0]?.brand,
          image: passedVouchers[0]?.image,
          totalCount: passedVouchers.length
        });
      } else {
        // Get data from URL params
        const response = await axios.get(`/vouchers/${id}`);
        const payload = response?.data?.data || response?.data || null;
        setVoucherData(payload);
        setMyVouchers(payload?.vouchers || []);
      }
    } catch (error) {
      console.error('Error fetching voucher details:', error);
      message.error('Failed to load voucher details');
    } finally {
      setLoading(false);
    }
  };

  // Copy function
  const handleCopy = async (text) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for browsers/contexts where Clipboard API is unavailable
        // (e.g. non-HTTPS pages on Safari).
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      message.success('Copied to clipboard');
    } catch (err) {
      console.error('Clipboard copy failed:', err);
      message.error('Failed to copy. Please copy manually.');
    }
  };

  // Export function
  const handleExport = () => {
    const exportData = myVouchers.map(voucher => {
      const row = {};
      exportSelectedFields.forEach(field => {
        switch (field) {
          case 'Code':
            row[field] = voucher.code || '';
            break;
          case 'Serial Number':
            row[field] = voucher.serialNumber || '';
            break;
          case 'PIN':
            row[field] = voucher.pin || '';
            break;
          case 'Issued At':
            row[field] = voucher.issuedAt ? dayjs(voucher.issuedAt).format('YYYY-MM-DD HH:mm') : '';
            break;
          case 'Order Number':
            row[field] = voucher.orderNumber || '';
            break;
          default:
            break;
        }
      });
      return row;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Voucher Codes');
    XLSX.writeFile(wb, `${voucherData?.voucherName}-codes-${dayjs().format('YYYY-MM-DD')}.xlsx`);
    message.success('Voucher codes exported successfully');
    setExportModalVisible(false);
  };

  // Table column definitions
  const columns = [
    {
      title: '#',
      dataIndex: 'index',
      key: 'index',
      width: 60,
      render: (_, __, index) => (
        <Text style={{ fontSize: '12px', color: '#666' }}>
          #{index + 1}
        </Text>
      )
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      render: (code) => (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          background: '#f8f9fa',
          padding: '8px 12px',
          borderRadius: '6px',
          border: '1px solid #e9ecef'
        }}>
          <Text code style={{ fontSize: '13px', flex: 1, fontFamily: 'monospace' }}>
            {code}
          </Text>
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={() => handleCopy(code)}
            style={{ padding: '2px 4px', minWidth: 'auto' }}
          />
        </div>
      )
    },
    {
      title: 'Serial Number',
      dataIndex: 'serialNumber',
      key: 'serialNumber',
      render: (serialNumber) => (
        serialNumber ? (
          <Text style={{ fontSize: '13px', color: '#666' }}>
            {serialNumber}
          </Text>
        ) : (
          <Text type="secondary" style={{ fontSize: '12px' }}>-</Text>
        )
      )
    },
    {
      title: 'PIN',
      dataIndex: 'pin',
      key: 'pin',
      render: (pin) => (
        pin ? (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            background: '#fff2e8',
            padding: '6px 10px',
            borderRadius: '6px',
            border: '1px solid #ffd591'
          }}>
            <Text code style={{ fontSize: '12px', flex: 1, fontFamily: 'monospace' }}>
              {pin}
            </Text>
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => handleCopy(pin)}
              style={{ padding: '2px 4px', minWidth: 'auto' }}
            />
          </div>
        ) : (
          <Text type="secondary" style={{ fontSize: '12px' }}>-</Text>
        )
      )
    },
    {
      title: 'Issued At',
      dataIndex: 'issuedAt',
      key: 'issuedAt',
      render: (issuedAt) => {
        if (!issuedAt) {
          return <Text type="secondary" style={{ fontSize: '12px' }}>—</Text>;
        }
        const d = dayjs(issuedAt);
        if (!d.isValid()) {
          return <Text type="secondary" style={{ fontSize: '12px' }}>—</Text>;
        }
        return (
          <div>
            <div style={{ fontSize: '13px', color: '#333' }}>
              {d.format('MMM DD, YYYY')}
            </div>
            <div style={{ fontSize: '11px', color: '#999' }}>
              {d.format('HH:mm')}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Order Number',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      render: (orderNumber) => (
        <Text style={{ fontSize: '13px', color: '#1890ff' }}>
          {orderNumber}
        </Text>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Button
          type="text"
          size="small"
          icon={<CopyOutlined />}
          onClick={() => handleCopy(`${record.voucherName} - ${record.code}${record.pin ? ` - PIN: ${record.pin}` : ''}`)}
          style={{ fontSize: '11px' }}
        >
          Copy All
        </Button>
      )
    }
  ];

  useEffect(() => {
    fetchVoucherDetails();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, fromMyVouchers, passedVouchers]);

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!voucherData || myVouchers.length === 0) {
    return (
      <div style={{ padding: 24 }}>
        <Result
          status="404"
          title="Voucher Not Found"
          subTitle="The voucher you're looking for doesn't exist or you don't have access to it."
          extra={
            <Button type="primary" onClick={() => navigate('/client/vouchers/my-vouchers')}>
              Back to My Vouchers
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      {/* Page header */}
      <div style={{ marginBottom: 24 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/client/vouchers/my-vouchers')}
          style={{ marginBottom: 16 }}
        >
          Back to My Vouchers
        </Button>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {/* Voucher image */}
          <div style={{ 
            width: 80, 
            height: 80, 
            borderRadius: 12,
            overflow: 'hidden',
            background: '#f8f9fa',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {voucherData.image ? (
              <Image
                src={voucherData.image}
                alt={voucherData.voucherName}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0zMiAyMEMyNS4zNzIgMjAgMjAgMjUuMzcyIDIwIDMyQzIwIDM4LjYyOCAyNS4zNzIgNDQgMzIgNDRDMzguNjI4IDQ0IDQ0IDM4LjYyOCA0NCAzMkM0NCAyNS4zNzIgMzguNjI4IDIwIDMyIDIwWiIgZmlsbD0iI0NDQyIvPgo8L3N2Zz4K"
              />
            ) : (
              <GiftOutlined style={{ fontSize: 32, color: '#ccc' }} />
            )}
          </div>
          
          {/* Voucher info */}
          <div style={{ flex: 1 }}>
            <Title level={3} style={{ margin: 0, marginBottom: 4 }}>
              {voucherData.voucherName}
            </Title>
            <Text style={{ fontSize: 16, color: '#666', marginBottom: 8 }}>
              {voucherData.brand}
            </Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px' }}>
                <GiftOutlined /> {voucherData.totalCount} vouchers
              </Tag>
              <Text style={{ fontSize: 14, color: '#999' }}>
                <CalendarOutlined /> Latest: {dayjs(myVouchers[0]?.issuedAt).format('MMM DD, YYYY')}
              </Text>
            </div>
          </div>
          
          {/* Action buttons */}
          <Space>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => setExportModalVisible(true)}
            >
              Export
            </Button>
          </Space>
        </div>
      </div>

      {/* Stats cards */}
      <Card 
        style={{ 
          marginBottom: 24, 
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          border: '1px solid #e2e8f0',
          borderRadius: 12
        }}
        bodyStyle={{ padding: '20px' }}
      >
        <Row gutter={24}>
          <Col xs={12} sm={6}>
            <Statistic
              title="Total Codes"
              value={myVouchers.length}
              prefix={<GiftOutlined />}
              valueStyle={{ color: '#1890ff', fontSize: 24 }}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="With PIN"
              value={myVouchers.filter(v => v.pin).length}
              prefix={<GiftOutlined />}
              valueStyle={{ color: '#52c41a', fontSize: 24 }}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="With Serial"
              value={myVouchers.filter(v => v.serialNumber).length}
              prefix={<GiftOutlined />}
              valueStyle={{ color: '#fa8c16', fontSize: 24 }}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="Unique Orders"
              value={new Set(myVouchers.map(v => v.orderNumber)).size}
              prefix={<GiftOutlined />}
              valueStyle={{ color: '#722ed1', fontSize: 24 }}
            />
          </Col>
        </Row>
      </Card>

      {/* Voucher codes list */}
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <EyeOutlined />
            <span>Voucher Codes</span>
            <Tag color="blue">{myVouchers.length} codes</Tag>
          </div>
        }
        style={{ borderRadius: 12 }}
        bodyStyle={{ padding: 0 }}
      >
        <Table
          columns={columns}
          dataSource={myVouchers.map((voucher, index) => ({ ...voucher, index }))}
          rowKey="_id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} codes`
          }}
          scroll={{ x: 800 }}
        />
      </Card>

      {/* Export modal */}
      <Modal
        title="Export Voucher Codes"
        open={exportModalVisible}
        onOk={handleExport}
        onCancel={() => setExportModalVisible(false)}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong>Select fields to export:</Text>
        </div>
        <Checkbox.Group
          value={exportSelectedFields}
          onChange={setExportSelectedFields}
          style={{ width: '100%' }}
        >
          <Row gutter={[16, 8]}>
            {exportFields.map(field => (
              <Col span={12} key={field.value}>
                <Checkbox value={field.value}>{field.label}</Checkbox>
              </Col>
            ))}
          </Row>
        </Checkbox.Group>
      </Modal>
    </div>
  );
};

export default VoucherDetails;
